// utils/timeUtils.js - FIXED VERSION for Thailand timezone issues
import moment from 'moment';
import 'moment/locale/th';

/**
 * Format timestamp for chat messages with proper Thailand timezone handling
 * Fixes the "อีก 7 ชั่วโมง" (in 7 hours) issue
 */
export const formatChatTimestamp = (timestamp, language = 'EN') => {
  if (!timestamp) return '';
  
  try {
    // Set locale
    const locale = language === 'TH' ? 'th' : 'en';
    moment.locale(locale);
    
    let momentTime;
    
    // Handle different timestamp formats and timezone issues
    if (typeof timestamp === 'string') {
      
      // Case 1: ISO string without timezone (like "2024-01-15T10:30:00")
      if (!timestamp.includes('Z') && 
          !timestamp.includes('+') && 
          !timestamp.includes('-', 10) && // Don't match date separators
          timestamp.includes('T')) {
        
        // Treat as local time first, then check if it makes sense
        momentTime = moment(timestamp);
        
        // If it's more than 1 hour in the future, it's probably a timezone issue
        const diffMinutes = moment().diff(momentTime, 'minutes');
        
        if (diffMinutes < -30) { // More than 30 minutes in the future
          // Try treating as UTC and converting to local
          momentTime = moment.utc(timestamp).local();
          
          // Still in future? Try subtracting common timezone offsets
          const newDiff = moment().diff(momentTime, 'minutes');
          if (newDiff < -30) {
            // Thailand is UTC+7, so if server sends UTC time but frontend treats as local,
            // we need to subtract 7 hours
            momentTime = moment(timestamp).subtract(7, 'hours');
          }
        }
        
      } 
      // Case 2: ISO string with Z (UTC)
      else if (timestamp.includes('Z')) {
        momentTime = moment.utc(timestamp).local();
      }
      // Case 3: ISO string with timezone offset
      else if (timestamp.includes('+') || timestamp.lastIndexOf('-') > 10) {
        momentTime = moment(timestamp);
      }
      // Case 4: Other string formats
      else {
        momentTime = moment(timestamp);
      }
      
    } else {
      // Handle Date objects or numbers
      momentTime = moment(timestamp);
    }
    
    // Final validation: if still more than 10 minutes in the future, something's wrong
    const finalDiff = moment().diff(momentTime, 'minutes');
    if (finalDiff < -10) {
      console.warn('⚠️ Timestamp still appears to be in the future:', {
        original: timestamp,
        parsed: momentTime.format(),
        diffMinutes: finalDiff
      });
      
      // Force it to be "just now" to prevent confusion
      momentTime = moment();
    }
    
    // Debug logging in development
    if (process.env.NODE_ENV === 'development') {
      console.log('🕐 Time Debug:', {
        original: timestamp,
        parsed: momentTime.format(),
        relative: momentTime.fromNow(),
        diffMinutes: moment().diff(momentTime, 'minutes'),
        isRecent: moment().diff(momentTime, 'minutes') < 5
      });
    }
    
    return momentTime.fromNow();
    
  } catch (error) {
    console.error('Error formatting timestamp:', error);
    return language === 'TH' ? 'เมื่อสักครู่' : 'just now';
  }
};

/**
 * Format timestamp for absolute time display
 */
export const formatAbsoluteTime = (timestamp, language = 'EN') => {
  if (!timestamp) return '';
  
  try {
    const locale = language === 'TH' ? 'th' : 'en';
    moment.locale(locale);
    
    // Use same parsing logic as formatChatTimestamp
    let momentTime;
    
    if (typeof timestamp === 'string') {
      if (!timestamp.includes('Z') && 
          !timestamp.includes('+') && 
          !timestamp.includes('-', 10) && 
          timestamp.includes('T')) {
        
        momentTime = moment(timestamp);
        const diffMinutes = moment().diff(momentTime, 'minutes');
        
        if (diffMinutes < -30) {
          momentTime = moment.utc(timestamp).local();
          const newDiff = moment().diff(momentTime, 'minutes');
          if (newDiff < -30) {
            momentTime = moment(timestamp).subtract(7, 'hours');
          }
        }
        
      } else if (timestamp.includes('Z')) {
        momentTime = moment.utc(timestamp).local();
      } else {
        momentTime = moment(timestamp);
      }
    } else {
      momentTime = moment(timestamp);
    }
    
    const now = moment();
    const diffDays = now.diff(momentTime, 'days');
    
    if (diffDays === 0) {
      return momentTime.format('HH:mm');
    } else if (diffDays === 1) {
      return language === 'TH' ? `เมื่อวาน ${momentTime.format('HH:mm')}` : `Yesterday ${momentTime.format('HH:mm')}`;
    } else if (diffDays < 7) {
      return momentTime.format('ddd HH:mm');
    } else {
      return momentTime.format('DD/MM/YY HH:mm');
    }
    
  } catch (error) {
    console.error('Error formatting absolute time:', error);
    return '';
  }
};

/**
 * Check if a timestamp is recent (less than 1 minute old)
 */
export const isRecentTimestamp = (timestamp) => {
  if (!timestamp) return false;
  
  try {
    // Use same parsing logic
    let momentTime;
    
    if (typeof timestamp === 'string') {
      if (!timestamp.includes('Z') && 
          !timestamp.includes('+') && 
          !timestamp.includes('-', 10) && 
          timestamp.includes('T')) {
        
        momentTime = moment(timestamp);
        const diffMinutes = moment().diff(momentTime, 'minutes');
        
        if (diffMinutes < -30) {
          momentTime = moment.utc(timestamp).local();
          const newDiff = moment().diff(momentTime, 'minutes');
          if (newDiff < -30) {
            momentTime = moment(timestamp).subtract(7, 'hours');
          }
        }
        
      } else if (timestamp.includes('Z')) {
        momentTime = moment.utc(timestamp).local();
      } else {
        momentTime = moment(timestamp);
      }
    } else {
      momentTime = moment(timestamp);
    }
    
    const diffSeconds = moment().diff(momentTime, 'seconds');
    return diffSeconds >= 0 && diffSeconds < 60; // Between 0 and 60 seconds ago
    
  } catch (error) {
    console.error('Error checking if timestamp is recent:', error);
    return false;
  }
};

/**
 * Get a properly formatted current timestamp for sending messages
 * Returns Thailand local time in ISO format
 */
export const getCurrentTimestamp = () => {
  // Return current time in Thailand timezone as ISO string
  return moment().toISOString();
};

/**
 * Parse timestamp and return moment object with proper timezone handling
 */
export const parseTimestamp = (timestamp) => {
  if (!timestamp) return moment();
  
  try {
    if (typeof timestamp === 'string') {
      if (!timestamp.includes('Z') && 
          !timestamp.includes('+') && 
          !timestamp.includes('-', 10) && 
          timestamp.includes('T')) {
        
        let momentTime = moment(timestamp);
        const diffMinutes = moment().diff(momentTime, 'minutes');
        
        if (diffMinutes < -30) {
          momentTime = moment.utc(timestamp).local();
          const newDiff = moment().diff(momentTime, 'minutes');
          if (newDiff < -30) {
            momentTime = moment(timestamp).subtract(7, 'hours');
          }
        }
        return momentTime;
        
      } else if (timestamp.includes('Z')) {
        return moment.utc(timestamp).local();
      } else {
        return moment(timestamp);
      }
    } else {
      return moment(timestamp);
    }
  } catch (error) {
    console.error('Error parsing timestamp:', error);
    return moment();
  }
};

// Quick fix function for immediate testing
export const quickFixTimestamp = (timestamp) => {
  if (!timestamp) return moment().toISOString();
  
  const parsed = moment(timestamp);
  const diffMinutes = moment().diff(parsed, 'minutes');
  
  // If it's in the future, force it to be current time
  if (diffMinutes < 0) {
    return moment().toISOString();
  }
  
  return timestamp;
};

const timeUtils = {
  formatChatTimestamp,
  formatAbsoluteTime,
  isRecentTimestamp,
  getCurrentTimestamp,
  parseTimestamp,
  quickFixTimestamp
};

export default timeUtils;